import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { useSnapshot } from "valtio";
import { Activity, Radio, RefreshCw, Server } from "lucide-react";

import { getHealthMetrics } from "../../../api/healthApi";
import { authStore } from "../../../stores/authStore";
import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";

/** Matches the dashboard cards; the backend caches the DB count for 30s anyway. */
const POLL_MS = 30_000;

const formatUptime = (seconds) => {
  if (!Number.isFinite(seconds)) return "-";
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days) return `${days}d ${hours}h`;
  if (hours) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

/** One number plus its label, so the cards stay consistent. */
const Metric = ({ value, label, primary = false }) => (
  <div>
    <div
      className={
        primary
          ? "text-3xl lg:text-4xl font-bold text-[#2B6A7F]"
          : "text-2xl font-semibold text-gray-700"
      }
    >
      {value ?? "-"}
    </div>
    <div className="text-xs text-gray-500">{label}</div>
  </div>
);

export default function AdminHealth() {
  const snap = useSnapshot(authStore);
  // Role string, not permissionStore.can() — an ADMIN can hold "*" permissions
  // and would pass a permission check while still being refused by the API.
  const isSuperAdmin = snap?.user?.role === "SUPER_ADMIN";

  const [metrics, setMetrics] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const intervalRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await getHealthMetrics();
      setMetrics(data);
      setError(null);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err?.message || "Failed to load health metrics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isSuperAdmin) return undefined;

    load();
    intervalRef.current = setInterval(load, POLL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isSuperAdmin, load]);

  // The route sits behind AdminRoute, which admits ADMIN too — so the page
  // itself has to turn plain admins away rather than show them a 403 loop.
  if (!isSuperAdmin) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const value = (v) => (metrics && !error && v !== undefined ? v : "-");
  const dependencies = Object.entries(metrics?.info ?? {});
  const healthy = metrics?.status === "ok";

  return (
    <div className="container mx-auto p-4 lg:p-6 space-y-4">
      {/* Status bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border bg-white px-4 py-3">
        <div className="flex items-center gap-3">
          <span
            className={`h-3 w-3 rounded-full ${
              error ? "bg-red-500" : healthy ? "bg-green-500" : "bg-amber-500"
            }`}
          />
          <div>
            <div className="font-semibold text-[#2B6A7F]">System Health</div>
            <div className="text-xs text-gray-500">
              {error
                ? error
                : loading
                  ? "Checking…"
                  : `All systems ${healthy ? "operational" : "degraded"}`}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {dependencies.map(([name, info]) => (
            <Badge
              key={name}
              variant={info.status === "up" ? "green" : "red"}
              className="text-xs capitalize"
              title={info.message || undefined}
            >
              {name} {info.status}
            </Badge>
          ))}
          <span className="text-xs text-gray-500">
            {lastUpdated
              ? `Updated ${lastUpdated.toLocaleTimeString()}`
              : "Not yet updated"}
          </span>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base lg:text-lg flex items-center gap-2">
              <Radio className="h-4 w-4 text-[#5D93A5]" />
              Active Users
            </CardTitle>
            <CardDescription className="text-sm">
              Live connections and recent API activity
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-6 flex-wrap">
              <Metric
                primary
                value={value(metrics?.users?.live)}
                label="connected now"
              />
              <Metric
                value={value(metrics?.users?.recentlyActive)}
                label={`active in the last ${metrics?.users?.windowMinutes ?? 15} min`}
              />
              <Metric
                value={value(metrics?.users?.liveConnections)}
                label="open sockets"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base lg:text-lg flex items-center gap-2">
              <Activity className="h-4 w-4 text-[#5D93A5]" />
              API Traffic
            </CardTitle>
            <CardDescription className="text-sm">
              This server instance
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-6 flex-wrap">
              <Metric
                primary
                value={value(metrics?.instance?.requests?.lastMinute)}
                label="requests / min"
              />
              <Metric
                value={value(metrics?.instance?.requests?.inFlight)}
                label="in flight"
              />
              <Metric
                value={value(metrics?.instance?.requests?.totalSinceBoot)}
                label="since restart"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base lg:text-lg flex items-center gap-2">
            <Server className="h-4 w-4 text-[#5D93A5]" />
            Instance
          </CardTitle>
          <CardDescription className="text-sm">
            Counters are per process and reset when the server restarts
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-6 flex-wrap">
            <Metric
              value={
                metrics && !error
                  ? formatUptime(metrics?.instance?.uptimeSeconds)
                  : "-"
              }
              label="uptime"
            />
            <Metric value={value(metrics?.instance?.pid)} label="process id" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
